import * as THREE from "three";
import { dom } from "../dom";
import { state, type SavedPlanView, type StoreyLevel } from "../state";
import { onLangChange, t } from "../i18n";
import { trackTool } from "../analytics/events";
import { rebuildMeshVisibility } from "../ifc/filters";
import { applyClipping } from "./clipping";
import { setFragmentsCamera } from "../fragments/engine";
import {
  camera,
  controls,
  planCamera,
  setActiveCamera,
  setPlanResizeHandler,
} from "../viewer/scene";

const savedPose = {
  position: new THREE.Vector3(),
  target: new THREE.Vector3(),
  near: 0.01,
  far: 5000,
  rotate: true,
};

let fitHalf = 10;

function sortedLevels(): StoreyLevel[] {
  return [...state.storeyLevels].sort((a, b) => a.elevation - b.elevation);
}

function levelsForPlan(): StoreyLevel[] {
  const filterStorey = state.activeFilters.storeyId;
  const levels = sortedLevels();
  if (!filterStorey) {
    return levels;
  }
  return levels.filter((level) => level.name === filterStorey);
}

function pickUpAxis(): 0 | 1 | 2 {
  const bounds = state.modelBounds;
  if (!bounds) {
    return 1;
  }
  const size = [0, 1, 2].map((axis) => bounds.max[axis] - bounds.min[axis]);
  const levels = sortedLevels();
  if (levels.length < 2) {
    let tallest: 0 | 1 | 2 = 1;
    if (size[0] >= size[tallest]) tallest = 0;
    if (size[2] >= size[tallest]) tallest = 2;
    if (size[1] >= size[tallest]) tallest = 1;
    return tallest;
  }
  const elevMin = Math.min(...levels.map((level) => level.elevation));
  const elevMax = Math.max(...levels.map((level) => level.elevation));
  const elevSpan = Math.abs(elevMax - elevMin);
  let best: 0 | 1 | 2 = 1;
  let bestScore = -Infinity;
  for (const axis of [0, 1, 2] as const) {
    const min = bounds.min[axis];
    const max = bounds.max[axis];
    const span = Math.max(max - min, 1e-6);
    const inside = levels.filter(
      (level) => level.elevation >= min - span && level.elevation <= max + span,
    ).length;
    const score = inside * 100 - Math.abs(span - elevSpan) / span;
    if (score > bestScore) {
      bestScore = score;
      best = axis;
    }
  }
  return best;
}

function cutHeight(storeyName: string, axis: 0 | 1 | 2): number {
  const bounds = state.modelBounds;
  if (!bounds) {
    return 0;
  }
  const levels = sortedLevels();
  const span = Math.max(bounds.max[axis] - bounds.min[axis], 1e-6);
  const insetRatio = 0.05;
  const idx = levels.findIndex((level) => level.name === storeyName);
  if (idx < 0 || levels.length === 0) {
    return bounds.max[axis] - span * insetRatio;
  }
  if (levels.length === 1) {
    return bounds.max[axis] - span * insetRatio;
  }
  if (idx < levels.length - 1) {
    const gap = levels[idx + 1].elevation - levels[idx].elevation;
    return levels[idx + 1].elevation - Math.abs(gap) * insetRatio;
  }
  const prevGap = levels[idx].elevation - levels[idx - 1].elevation;
  return bounds.max[axis] - Math.abs(prevGap) * insetRatio;
}

function applyOrthoFrustum(): void {
  const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
  planCamera.top = fitHalf;
  planCamera.bottom = -fitHalf;
  planCamera.left = -fitHalf * aspect;
  planCamera.right = fitHalf * aspect;
  planCamera.updateProjectionMatrix();
}

function placePlanCamera(box: THREE.Box3, axis: 0 | 1 | 2): void {
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const horizontal =
    axis === 1 ? Math.max(size.x, size.z) : axis === 2 ? Math.max(size.x, size.y) : Math.max(size.y, size.z);
  fitHalf = Math.max(horizontal, 0.5) * 0.6;
  planCamera.zoom = 1;
  const distance = Math.max(size.x, size.y, size.z, 1) * 2;
  const position = center.clone();
  position.setComponent(axis, box.max.getComponent(axis) + distance);
  planCamera.position.copy(position);
  if (axis === 1) {
    planCamera.up.set(0, 0, -1);
  } else if (axis === 2) {
    planCamera.up.set(0, 1, 0);
  } else {
    planCamera.up.set(0, 0, 1);
  }
  planCamera.near = 0.01;
  planCamera.far = distance * 6;
  planCamera.lookAt(center);
  applyOrthoFrustum();
  const up = planCamera.up.clone();
  controls.target.copy(center);
  controls.update();
  planCamera.position.copy(position);
  planCamera.up.copy(up);
  planCamera.lookAt(center);
  planCamera.updateProjectionMatrix();
}

export function centerPlanView(): void {
  if (!state.planView.active) {
    return;
  }
  framePlan(pickUpAxis());
}

function framePlan(axis: 0 | 1 | 2): void {
  const bounds = state.modelBounds;
  if (!bounds) {
    return;
  }
  const box = new THREE.Box3(
    new THREE.Vector3(bounds.min[0], bounds.min[1], bounds.min[2]),
    new THREE.Vector3(bounds.max[0], bounds.max[1], bounds.max[2]),
  );
  if (box.isEmpty()) {
    return;
  }
  placePlanCamera(box, axis);
}

function passingCount(storeyName: string): number {
  let count = 0;
  for (const id of state.geometryExpressIds || []) {
    if (state.hiddenExpressIds.has(id)) {
      continue;
    }
    const meta = state.elementMeta.get(id);
    if (storeyName && meta?.storeyName && meta.storeyName !== storeyName) {
      continue;
    }
    if (state.activeFilters.storeyId && meta?.storeyName !== state.activeFilters.storeyId) {
      continue;
    }
    if (state.activeFilters.types.length > 0 && !state.activeFilters.types.includes(meta?.type || "")) {
      continue;
    }
    if (state.activeFilters.mark) {
      const haystack = `${meta?.mark || ""}`.toLowerCase();
      if (!haystack.includes(state.activeFilters.mark.toLowerCase())) {
        continue;
      }
    }
    count += 1;
  }
  return count;
}

function renderPlanUi(): void {
  const open = state.planView.active;
  dom.planOpenButton.disabled = !state.ifcModel;
  dom.planOpenButton.textContent = open ? t("plan.close") : t("plan.open");
  dom.planOpenButton.classList.toggle("is-active", open);

  const levels = levelsForPlan();
  dom.planStoreyList.replaceChildren();
  if (!state.ifcModel) {
    dom.planStatus.textContent = t("plan.needModel");
    return;
  }
  if (levels.length === 0) {
    dom.planStatus.textContent = t("plan.noStoreys");
    return;
  }
  for (const level of levels) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "plan-storey-btn";
    button.classList.toggle("is-active", open && level.name === state.planView.storeyName);
    button.textContent = level.name;
    button.disabled = !open;
    button.addEventListener("click", () => {
      selectPlanStorey(level.name);
    });
    dom.planStoreyList.appendChild(button);
  }
  if (!open) {
    dom.planStatus.textContent = t("plan.idle");
    return;
  }
  const visible = passingCount(state.planView.storeyName);
  dom.planStatus.textContent =
    visible === 0
      ? t("plan.emptyFilter")
      : t("plan.status", { name: state.planView.storeyName || t("plan.wholeModel") });
}

function applyCurrentStorey(): void {
  const axis = pickUpAxis();
  const cut = cutHeight(state.planView.storeyName, axis);
  state.planView.cutAxis = axis;
  state.planView.cut = cut;
  applyClipping();
  framePlan(axis);
  rebuildMeshVisibility();
}

export function capturePlanBookmark(): SavedPlanView {
  return {
    storeyName: state.planView.storeyName,
    cutAxis: state.planView.cutAxis,
    cut: state.planView.cut,
    zoom: planCamera.zoom,
    fitHalf,
    position: [planCamera.position.x, planCamera.position.y, planCamera.position.z],
    target: [controls.target.x, controls.target.y, controls.target.z],
    up: [planCamera.up.x, planCamera.up.y, planCamera.up.z],
  };
}

export function applyPlanBookmark(bookmark: SavedPlanView): void {
  if (!state.ifcModel || !state.modelBounds) {
    return;
  }
  if (!state.planView.active) {
    savedPose.position.copy(camera.position);
    savedPose.target.copy(controls.target);
    savedPose.near = camera.near;
    savedPose.far = camera.far;
    savedPose.rotate = controls.enableRotate;
    state.planView.active = true;
    controls.enableRotate = false;
    setActiveCamera(planCamera);
    setFragmentsCamera(planCamera);
  }
  state.planView.storeyName = bookmark.storeyName;
  state.planView.cutAxis = bookmark.cutAxis === 0 || bookmark.cutAxis === 2 ? bookmark.cutAxis : 1;
  state.planView.cut = bookmark.cut;
  fitHalf = bookmark.fitHalf > 0 ? bookmark.fitHalf : fitHalf;
  planCamera.zoom = bookmark.zoom > 0 ? bookmark.zoom : 1;
  planCamera.position.set(bookmark.position[0], bookmark.position[1], bookmark.position[2]);
  planCamera.up.set(bookmark.up[0], bookmark.up[1], bookmark.up[2]);
  planCamera.near = 0.01;
  planCamera.far = Math.max(planCamera.far, 5000);
  applyOrthoFrustum();
  controls.target.set(bookmark.target[0], bookmark.target[1], bookmark.target[2]);
  controls.update();
  planCamera.position.set(bookmark.position[0], bookmark.position[1], bookmark.position[2]);
  planCamera.up.set(bookmark.up[0], bookmark.up[1], bookmark.up[2]);
  planCamera.lookAt(controls.target);
  planCamera.updateProjectionMatrix();
  applyClipping();
  rebuildMeshVisibility();
  renderPlanUi();
}

export function refitPlanToBox(box: THREE.Box3): void {
  if (!state.planView.active || box.isEmpty()) {
    return;
  }
  placePlanCamera(box, pickUpAxis());
}

export function syncPlanWithFilters(): void {
  if (state.planView.active && state.activeFilters.storeyId) {
    state.planView.storeyName = state.activeFilters.storeyId;
    applyCurrentStorey();
  }
  renderPlanUi();
}

export function refreshPlanUi(): void {
  if (state.planView.active) {
    const levels = levelsForPlan();
    if (state.activeFilters.storeyId) {
      state.planView.storeyName = state.activeFilters.storeyId;
    } else if (!levels.some((level) => level.name === state.planView.storeyName)) {
      state.planView.storeyName = levels[0]?.name || "";
    }
    applyCurrentStorey();
  }
  renderPlanUi();
}

function selectPlanStorey(name: string): void {
  if (!state.planView.active || state.planView.storeyName === name) {
    return;
  }
  state.planView.storeyName = name;
  applyCurrentStorey();
  renderPlanUi();
}

export function exitPlanView(options?: { restoreVisibility?: boolean }): void {
  if (!state.planView.active) {
    renderPlanUi();
    return;
  }
  state.planView.active = false;
  camera.position.copy(savedPose.position);
  camera.near = savedPose.near;
  camera.far = savedPose.far;
  camera.updateProjectionMatrix();
  controls.target.copy(savedPose.target);
  controls.enableRotate = savedPose.rotate;
  setActiveCamera(camera);
  setFragmentsCamera(camera);
  controls.update();
  applyClipping();
  if (options?.restoreVisibility !== false) {
    rebuildMeshVisibility();
  }
  renderPlanUi();
}

export function enterPlanView(): void {
  if (!state.ifcModel || !state.modelBounds || state.planView.active) {
    return;
  }
  const levels = levelsForPlan();
  state.planView.storeyName = state.activeFilters.storeyId || levels[0]?.name || "";
  savedPose.position.copy(camera.position);
  savedPose.target.copy(controls.target);
  savedPose.near = camera.near;
  savedPose.far = camera.far;
  savedPose.rotate = controls.enableRotate;
  state.planView.active = true;
  controls.enableRotate = false;
  setActiveCamera(planCamera);
  setFragmentsCamera(planCamera);
  applyCurrentStorey();
  renderPlanUi();
  trackTool("plan");
}

export function initPlanView(): void {
  setPlanResizeHandler(() => {
    if (state.planView.active) {
      applyOrthoFrustum();
    }
  });
  dom.planOpenButton.addEventListener("click", () => {
    if (state.planView.active) {
      exitPlanView();
    } else {
      enterPlanView();
    }
  });
  onLangChange(() => {
    renderPlanUi();
  });
  renderPlanUi();
}
